"use client";

import { useEffect, useRef, useState } from "react";
import AppImage from "@/components/ui/AppImage";
import { partnerService, type Partner } from "@/lib/services/partnerService";
import NavBar from "@/app/home-page/components/NavBar";
import FooterSection from "@/app/home-page/components/FooterSection";

export default function PartnersPage() {
  const heroRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPartners = async () => {
      try {
        const data = await partnerService.getActive();
        setPartners(data);
      } catch (error) {
        console.error("Failed to load partners:", error);
      } finally {
        setLoading(false);
      }
    };
    loadPartners();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible", "animate-fade-up");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    if (heroRef.current) observer.observe(heroRef.current);
    if (gridRef.current) observer.observe(gridRef.current);
    return () => observer.disconnect();
  }, [loading]);

  return (
    <>
      <NavBar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-white to-gray-50 border-b border-border-gray">
        <div className="max-w-7xl mx-auto px-6">
          <div ref={heroRef} className="on-scroll text-center">
            <div className="section-label mx-auto w-fit mb-6">Our Trusted Partners</div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-deep-navy mb-6">
              Built Together, Delivered Better
            </h1>
            <p className="text-body-text text-lg max-w-2xl mx-auto">
              We&apos;re proud to collaborate with forward-thinking organizations who share our vision.
              Because great communities are built on great partnerships.
            </p>
          </div>
        </div>
      </section>

      {/* Partners Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="w-12 h-12 border-4 border-brand-purple border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : partners.length === 0 ? (
            <div className="text-center py-24">
              <p className="text-body-text text-lg">No partners to display at this time.</p>
            </div>
          ) : (
            <div ref={gridRef} className="on-scroll grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 items-center">
              {partners.map((partner) => (
                <a
                  key={partner.id}
                  href={partner.websiteUrl || "#"}
                  target={partner.websiteUrl ? "_blank" : "_self"}
                  rel="noopener noreferrer"
                  className="flex items-center justify-center p-6 rounded-3xl border border-border-gray hover:border-brand-purple/30 hover:shadow-purple-sm transition-all duration-300 hover:scale-105 hover:shadow-lg group"
                  aria-label={`Visit ${partner.name} website`}
                >
                  <AppImage
                    src={partner.logoUrl}
                    alt={`${partner.name} logo`}
                    width={160}
                    height={60}
                    className="partner-logo h-12 w-auto object-contain group-hover:filter-none"
                  />
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-50 border-t border-border-gray">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-deep-navy mb-4">
            Interested in Partnering With Us?
          </h2>
          <p className="text-body-text mb-8 text-lg">
            Join our growing network of partners and help shape the future of urban logistics.
          </p>
          <a
            href="/contact-us"
            className="btn-primary inline-block"
          >
            Get in Touch
          </a>
        </div>
      </section>

      <FooterSection />
    </>
  );
}
